import { adminClient, emailOTPClient, inferAdditionalFields, magicLinkClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import type { BetterAuthClientOptions } from "better-auth/client";
import { customSessionClient, genericOAuthClient, organizationClient } from "better-auth/client/plugins";
import type { ActiveMemberData, AuthSessionData, AuthType } from "./lib/auth";
import { ac, owner, staff, admin as adminPermission, designer } from "./lib/permissions";
export { passwordSchema } from "./lib/password";
export { isOrganizationOwner } from "./lib/permissions";

export type BetterAuthClientOnErrorContext = Parameters<
  NonNullable<NonNullable<BetterAuthClientOptions["fetchOptions"]>["onError"]>
>[0];

export type { BetterAuthClientOptions };
export type { AuthSessionData as AuthSession, ActiveMemberData };

export const authClient = createAuthClient({
  baseURL: process.env.JURNI_AUTH_URL,
  plugins: [
    adminClient({}),
    emailOTPClient(),
    magicLinkClient(),
    genericOAuthClient(),
    customSessionClient<AuthType>(),
    inferAdditionalFields<AuthType>(),
    organizationClient({
      ac,
      roles: {
        staff,
        owner,
        admin: adminPermission,
        designer,
      },
    }),
  ],
});

export const { useSession } = authClient;

const GET_SESSION_PATH = "/get-session?disableCookieCache=true";

interface AuthFetchOptions {
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  path: string;
  cookie?: string | null;
  body?: Record<string, any>;
  retries?: number;
}

const authFetchWithResponse = async <T>({
  path,
  cookie,
  method = "GET",
  body,
  retries = 0,
}: AuthFetchOptions): Promise<{ data: T | null; response: Response } | null> => {
  try {
    const response = await fetch(`${process.env.JURNI_AUTH_URL}/api/auth${path}`, {
      method,
      credentials: cookie ? undefined : "include",
      headers: {
        ...(cookie ? { cookie } : {}),
        ...(body
          ? {
              "Content-Type": "application/json",
              origin: process.env.JURNI_AUTH_URL!,
            }
          : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const text = await response.text();
    return {
      data: text ? (JSON.parse(text) as T) : null,
      response,
    };
  } catch (error) {
    console.error(
      `Error in auth fetch (remaining retries: ${retries}): ${error instanceof Error ? error.stack : error}`,
      error,
    );
    if (retries > 0) {
      return await new Promise((resolve) => setTimeout(resolve, 100)).then(() =>
        authFetchWithResponse<T>({
          path,
          cookie,
          method,
          body,
          retries: retries - 1,
        }),
      );
    }
    return null;
  }
};

const authFetch = async <T>(options: AuthFetchOptions): Promise<T | null> => {
  const result = await authFetchWithResponse<T>(options);
  return result?.data ?? null;
};

export const authFetcher = {
  async getSession({ cookie }: { cookie?: string | null }) {
    return authFetch<AuthSessionData>({
      method: "GET",
      path: GET_SESSION_PATH,
      cookie,
      retries: 1,
    });
  },
  async getSessionWithResponseCookies({ cookie }: { cookie?: string | null }) {
    const result = await authFetchWithResponse<AuthSessionData>({
      method: "GET",
      path: GET_SESSION_PATH,
      cookie,
      retries: 1,
    });
    return {
      session: result?.data ?? null,
      responseCookies: result?.response.headers.getSetCookie() ?? [],
    };
  },
  async getActiveMember({ cookie }: { cookie?: string | null }) {
    return authFetch<ActiveMemberData>({
      method: "GET",
      path: "/organization/get-active-member",
      cookie,
      retries: 1,
    });
  },
  async hasPermission({ cookie, permissions }: { cookie?: string | null; permissions: Record<string, string[]> }) {
    return authFetch<{ success: boolean; error: string | null }>({
      method: "POST",
      path: "/organization/has-permission",
      cookie,
      body: { permissions },
    });
  },
  async oauth2({
    providerId,
    callbackURL,
    cookie,
  }: {
    providerId: string;
    callbackURL: string;
    cookie?: string | null;
  }) {
    return authFetch<{ url: string }>({
      method: "POST",
      path: "/sign-in/oauth2",
      body: { providerId, callbackURL },
      cookie,
    });
  },
  async oauth2Consent({
    accept,
    consentCode,
    cookie,
  }: {
    accept: boolean;
    consentCode: string;
    cookie?: string | null;
  }) {
    return authFetch<{ redirectURI?: string; message?: string }>({
      method: "POST",
      path: "/oauth2/consent",
      body: { accept, consent_code: consentCode },
      cookie,
    });
  },
};
